import React, { useEffect, useState, useMemo, useCallback } from "react"
import { MaterialReactTable, useMaterialReactTable } from "material-react-table"
import { TextField, createTheme, ThemeProvider, useTheme, Box, IconButton, TablePagination } from "@mui/material"
import CustomTooltip from "../../../components/ToolTip"
import DetalleIcon from "../../../assets/iconos/Detalle.ico"
import { esES } from "@mui/material/locale"
import { MRT_Localization_ES } from "material-react-table/locales/es"
import { useNavigate } from "react-router-dom"
import ImpresionIcon from "../../../assets/iconos/Impresion.ico"
import { PickingContextProvider } from "../PickingContext"
import normalFormatDate from "../../utils/date/DDMMYYYFormatDate"
import normalFormatHour from "../../utils/date/HHMMSSFormatHour"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import fetchwrapper from "../../../services/interceptors/fetchwrapper"
import { useQuery } from "@tanstack/react-query"
import DetalleImpresionSearch from "./DetalleImpresionSearch"

const CustomTable = ({
  fetchedInfo = [],
  isLoadingInfoError,
  isLoadingInfo,
  isFetchingInfo,
  searchAttempted,
  totalPages,
  refetch,
  onFilterChange,
  onPageChange,
  currentPage,
}) => {
  const [tranumero, setTraNumero] = useState("")
  // impresion
  const [openModal, setOpenModal] = useState(false)
  const [infoGenerada, setInfoGenerada] = useState({})

  const handleCloseModal = () => {
    setOpenModal(false)
  }

  const handleOpenModal = () => {
    console.log("imprimiendoooo")
    setOpenModal(true)
    console.log("Estado del modal:", openModal)
  }

  // impresion
  const {
    data: fetchedSpecificPicking = {},
    isError: isLoadingSpecificPickingError,
    isFetching: isFetchingSpecificPicking,
    isLoading: isLoadingSpecificPicking,
    refetch: refetchSpecificPicking,
  } = useGetSolicitud()

  useEffect(() => {
    if (fetchedSpecificPicking && Object.keys(fetchedSpecificPicking).length > 0) {
      setInfoGenerada(fetchedSpecificPicking)
    }
  }, [fetchedSpecificPicking])

  useEffect(() => {
    if (tranumero) {
      imprimir() // Ejecuta imprimir cuando tranumero cambia
    }
  }, [tranumero])

  function useGetSolicitud() {
    return useQuery({
      queryKey: ["getSpecificPicking"],
      queryFn: async ({ queryParams }) => {
        const options = {
          method: "POST",
          body: JSON.stringify({
            tranumero, // Aquí pasamos el tranumero a la API
          }),
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          },
        }
        // send api request here
        let response = await fetchwrapper("/picking/getSpecificPicking", options)
        response = await response.json()
        return response.data
      },
      refetchOnWindowFocus: false,
      enabled: false,
    })
  }

  const imprimir = async () => {
    await refetchSpecificPicking()
    handleOpenModal()
  }

  const [filters, setFilters] = useState({})
  const [page, setPage] = useState(1)
  const [trafecha, setTraFecha] = useState(null)

  // Maneja cambios en los filtros de la grilla
  const handleFilterChange = (columnId, value) => {
    onFilterChange(columnId, value)
  }

  useEffect(() => {
    setPage(currentPage) // Sincroniza la página interna con el prop currentPage
  }, [currentPage])

  const handleFechaTransaccion = (newFecha) => {
    setTraFecha(newFecha)
    if (newFecha) {
      handleFilterChange("trafecha", newFecha.format("YYYY-MM-DD"))
    } else {
      handleFilterChange("trafecha", "")
    }
  }

  // Actualiza la página al cambiar el número de página en la paginación
  const handlePageChange = useCallback(
    (event, newPage) => {
      setPage(newPage + 1)
      onPageChange(newPage + 1)
    },
    [onPageChange],
  )

  useEffect(() => {
    setPage(page)
  }, [page])

  const columns = useMemo(
    () => [
      {
        accessorKey: "acciones",
        header: "Acciones",
        enableColumnFilter: false,
        size: 150,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", gap: "1rem" }}>
            <CustomTooltip title="Imprimir">
              <IconButton
                onClick={() => {
                  setTraNumero(row.original.tranumero)
                }}
              >
                <img src={ImpresionIcon} alt="Imprimir" style={{ width: "40px" }} />
              </IconButton>
              <DetalleImpresionSearch open={openModal} handleClose={handleCloseModal} infoGenerada={infoGenerada} />
            </CustomTooltip>
          </Box>
        ),
      },
      {
        accessorKey: "tranumero",
        header: "Egreso",
        enableEditing: false,
        size: 200,
        Filter: ({ column }) => (
          <TextField
            variant="standard"
            placeholder="Filtrar"
            fullWidth
            InputProps={{ style: { padding: "8px" } }}
            onChange={(e) => handleFilterChange(column.id, e.target.value)}
            sx={{
              width: 100,
              "& .MuiInputBase-input": {
                padding: "8px",
              },
            }}
          />
        ),
      },
      {
        accessorKey: "trafecha",
        header: "Fecha",
        enableEditing: false,
        size: 200,
        Cell: ({ cell }) => {
          const cellValue = cell.getValue()
          return normalFormatDate(cellValue)
        },
        Filter: ({ column }) => (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={trafecha}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  variant: "filled",
                  sx: {
                    "& .MuiFilledInput-root": {
                      backgroundColor: "transparent",
                    },
                  },
                },
              }}
              onChange={handleFechaTransaccion}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            />
          </LocalizationProvider>
        ),
      },
      {
        accessorKey: "trahora",
        header: "Hora",
        enableEditing: false,
        size: 120,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const cellValue = cell.getValue()
          return normalFormatHour(cellValue)
        },
      },
      {
        accessorKey: "pctacodigo",
        header: "Ingreso",
        enableEditing: false,
        size: 120,
        Filter: ({ column }) => (
          <TextField
            variant="standard"
            placeholder="Filtrar"
            fullWidth
            InputProps={{ style: { padding: "8px" } }}
            onChange={(e) => handleFilterChange(column.id, e.target.value)}
            sx={{
              width: 100,
              "& .MuiInputBase-input": {
                padding: "8px",
              },
            }}
          />
        ),
      },
      {
        accessorKey: "tradocumento",
        header: "Transferencia",
        enableEditing: false,
        size: 120,
        Filter: ({ column }) => (
          <TextField
            variant="standard"
            placeholder="Filtrar"
            fullWidth
            InputProps={{ style: { padding: "8px" } }}
            onChange={(e) => handleFilterChange(column.id, e.target.value)}
            sx={{
              width: 100,
              "& .MuiInputBase-input": {
                padding: "8px",
              },
            }}
          />
        ),
      },
      {
        accessorKey: "detalle_ordnumero",
        header: "Picking",
        enableEditing: false,
        size: 120,
        Filter: ({ column }) => (
          <TextField
            variant="standard"
            placeholder="Filtrar"
            fullWidth
            InputProps={{ style: { padding: "8px" } }}
            onChange={(e) => handleFilterChange(column.id, e.target.value)}
            sx={{
              width: 100,
              "& .MuiInputBase-input": {
                padding: "8px",
              },
            }}
          />
        ),
      },
      {
        accessorKey: "tradescri",
        header: "Descripción",
        enableEditing: false,
        size: 120,
        Filter: ({ column }) => (
          <TextField
            variant="standard"
            placeholder="Filtrar"
            fullWidth
            InputProps={{ style: { padding: "8px" } }}
            onChange={(e) => handleFilterChange(column.id, e.target.value)}
            sx={{
              width: 100,
              "& .MuiInputBase-input": {
                padding: "8px",
              },
            }}
          />
        ),
      },
    ],
    [trafecha],
  )

  const table = useMaterialReactTable({
    columns,
    data: fetchedInfo,
    enableEditing: false,
    enableRowActions: false,
    localization: { ...MRT_Localization_ES },
    initialState: {
      showColumnFilters: true,
    },
    getRowId: (row) => row.formcodigo,
    muiToolbarAlertBannerProps:
      searchAttempted && !isLoadingInfo && fetchedInfo.length === 0 && !isFetchingInfo
        ? {
            color: "info",
            children: "No se encontraron solicitudes",
          }
        : undefined,
    muiTableContainerProps: {
      sx: {
        minHeight: "500px",
      },
    },
    enablePagination: false,
    renderToolbarInternalActions: ({ table }) => <Box></Box>,
    renderBottomToolbarCustomActions: () => (
      <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", padding: "0.5rem" }}>
        <TablePagination
          rowsPerPageOptions={[10]}
          component="div"
          count={totalPages}
          rowsPerPage={10}
          page={page - 1}
          onPageChange={handlePageChange}
        />
      </Box>
    ),
    state: {
      isLoading: isLoadingInfo,
      showAlertBanner: isLoadingInfoError,
      showProgressBars: isFetchingInfo,
    },
  })

  return (
    <>
      <MaterialReactTable table={table} />
      <DetalleImpresionSearch open={openModal} handleClose={handleCloseModal} infoGenerada={infoGenerada} />
    </>
  )
}

const GridPickingSearch = ({
  refetch,
  fetchedInfo,
  isLoadingInfo,
  isError,
  isFetchingInfo,
  totalPages,
  onPageChange,
  onFilterChange,
}) => {
  const theme = useTheme()

  // Maneja cambios en los filtros de la grilla
  const handleFilterChange = (columnId, value) => {
    onFilterChange(columnId, value)
  }

  return (
    <div>
      <PickingContextProvider>
        <ThemeProvider theme={createTheme(theme, esES)}>
          <CustomTable
            fetchedInfo={fetchedInfo}
            isLoadingInfoError={isError}
            isLoadingInfo={isLoadingInfo}
            isFetchingInfo={isFetchingInfo}
            totalPages={totalPages}
            onPageChange={onPageChange}
            refetch={refetch}
            onFilterChange={handleFilterChange}
          />
        </ThemeProvider>
      </PickingContextProvider>
    </div>
  )
}

export default GridPickingSearch
